import { useCallback, useContext } from 'react'
import { useDispatch } from 'react-redux'
import { useLocation } from 'react-router'
import { push } from 'connected-react-router'
import { contexts } from '@ocean/ui'
import { HelpUtils } from '@ocean/utils'
import { LegacyMenuRedirectRoute, LegacyMenuVirtualTrainerModuleName } from 'types'
import { useLazySelector } from 'hooks'
import { logoutUser } from 'core/session/slices/session'
import { redirectToSta } from 'core/staIntegration/slices/staIntegration'
import { StaRedirectParams } from 'core/staIntegration/types'
import { AppLayout, AppLayoutSegment } from 'layouts'
import { Sidebar } from 'components/general'
import { SectionsNav } from 'components/navigation'
import assessmentPlatformRoutes from 'modules/AssessmentPlatform/routes'
import homeRoutes from 'modules/Home/routing/routes'
import {
  EventNotificationsContainer,
  MaintenanceNotificationsContainer,
  BannerNotificationsContainer,
} from 'modules/Notifications/containers'
import programBuilderRoutes from 'modules/programBuilder/routes'
import simulatorTrainingInstructorILSimulatorListRoutes from 'modules/simulatorTrainings/instructor/instructorLed/SimulatorList/routing/routes'
import simulatorTrainingInstructorSLExerciseListRoutes from 'modules/simulatorTrainings/instructor/studentLed/ExerciseList/routing/routes'
import virtualClassroomEventCreatorRoutes from 'modules/virtualClassrooms/manager/EventCreator/routing/routes'
import virtualClassroomEventListRoutes from 'modules/virtualClassrooms/manager/EventList/routing/routes'
import virtualClassroomStudentEventsRoutes from 'modules/virtualClassrooms/student/Events/routing/routes'

const { FullscreenContext } = contexts

const AppLayer: React.FC = ({ children }) => {
  const dispatch = useDispatch()
  const { pathname } = useLocation()
  const { isFullscreenEnabled } = useContext(FullscreenContext) || {}
  const { userName, legacyMenuItems, notificationsCounters, logoTimestamp } = useLazySelector(
    ({ auth, staIntegration, environment, system }) => {
      const { firstName, lastName } = auth.userAccount?.connectedPerson || {}
      const userName = `${firstName || ''} ${lastName || ''}`

      return {
        userName,
        legacyMenuItems:
          staIntegration.legacyMenuConfiguration.result?.configuration.legacyMenuItems,
        notificationsCounters: environment.notificationsCounters.result?.counters,
        logoTimestamp:
          system.customerInformation.result?.information.customization.menuLogoTimeStamp,
      }
    }
  )
  const showMenuBar =
    !isFullscreenEnabled &&
    pathname !== programBuilderRoutes.root &&
    pathname !== assessmentPlatformRoutes.root

  function handleLogout() {
    dispatch(logoutUser())
  }

  const handleRedirect = useCallback(
    (redirectParams: StaRedirectParams) => {
      const { pluginName, moduleName } = redirectParams

      switch (pluginName) {
        case LegacyMenuRedirectRoute.Help:
          window.open(HelpUtils.getHelpLink(), '_blank')
          break
        case LegacyMenuRedirectRoute.MyEvents:
          dispatch(push(virtualClassroomStudentEventsRoutes.my))
          break
        case LegacyMenuRedirectRoute.EventLibrary:
          dispatch(push(virtualClassroomStudentEventsRoutes.library))
          break
        case LegacyMenuRedirectRoute.InstructorLed:
          dispatch(push(simulatorTrainingInstructorILSimulatorListRoutes.root))
          break
        case LegacyMenuRedirectRoute.StudentLed:
          dispatch(push(simulatorTrainingInstructorSLExerciseListRoutes.root))
          break
        case LegacyMenuRedirectRoute.VirtualTrainerPlugin:
          if (moduleName === LegacyMenuVirtualTrainerModuleName.AddVirtualEvent)
            dispatch(push(virtualClassroomEventCreatorRoutes.root))
          if (moduleName === LegacyMenuVirtualTrainerModuleName.ViewVirtualEvents)
            dispatch(push(virtualClassroomEventListRoutes.root))
          break
        case LegacyMenuRedirectRoute.Home:
          dispatch(push(homeRoutes.root))
          break
        default:
          dispatch(redirectToSta(redirectParams))
      }
    },
    [dispatch]
  )

  return (
    <AppLayout>
      {showMenuBar ? (
        <AppLayoutSegment segmentId='sidebar'>
          <Sidebar
            onLogout={handleLogout}
            onRedirect={handleRedirect}
            userName={userName}
            legacyMenuItems={legacyMenuItems}
            notificationsCounters={notificationsCounters}
            logoTimestamp={logoTimestamp}
          />
        </AppLayoutSegment>
      ) : (
        <></>
      )}
      <AppLayoutSegment segmentId='banner'>
        <MaintenanceNotificationsContainer />
        <BannerNotificationsContainer />
        <EventNotificationsContainer />
      </AppLayoutSegment>
      <AppLayoutSegment segmentId='content'>{children}</AppLayoutSegment>
      {showMenuBar ? (
        <AppLayoutSegment segmentId='mobilebar'>
          <SectionsNav
            legacyMenuItems={legacyMenuItems}
            notificationsCounters={notificationsCounters}
            onRedirect={handleRedirect}
          />
        </AppLayoutSegment>
      ) : (
        <></>
      )}
    </AppLayout>
  )
}

export default AppLayer
